import { ref } from 'vue'
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth'
import axios from 'axios'

/**
 * Sign-up composable for backend-controlled Firebase user creation with email verification
 *
 * This composable handles sign-up flow where:
 * 1. User submits all info (email, password, display name, org name)
 * 2. Backend creates Firebase user and sends verification email
 * 3. User clicks link in email to verify
 * 4. User signs in (backend checks email is verified)
 *
 * For existing user sign-in, use useAuth.ts instead.
 */

interface EmailSignUpResponse {
  message: string
  member_id: number
  organization_id: number
  project_id: number
}

interface GoogleSignUpResponse {
  custom_token: string
  member_id: number
  organization_id: number
  project_id: number
  message: string
}

export function useSignUp() {
  const auth = getAuth()

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Sign up with email - all in one step
   * Creates Firebase user, sends verification email, creates backend member, org, and project
   */
  const signUpWithEmail = async (params: {
    email: string
    password: string
    displayName: string
    organizationName: string
    projectName: string
  }): Promise<EmailSignUpResponse | null> => {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.post<EmailSignUpResponse>('/api/3/sign-up/email', {
        email: params.email,
        password: params.password,
        display_name: params.displayName,
        organization_name: params.organizationName,
        project_name: params.projectName
      })

      return response.data
    } catch (err: any) {
      console.error('[useSignUp] Email sign-up error:', err)
      // Handle both string and object error details
      const detail = err.response?.data?.detail
      if (typeof detail === 'string') {
        error.value = detail
      } else if (typeof detail === 'object' && detail?.message) {
        error.value = detail.message
      } else {
        error.value = err.message || 'Failed to sign up'
      }
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sign up with Google OAuth
   * Combines all steps into one for Google sign-up
   */
  const signUpWithGoogle = async (params: {
    displayName: string
    organizationName: string
    projectName: string
  }): Promise<GoogleSignUpResponse | null> => {
    try {
      isLoading.value = true
      error.value = null

      // Step 1: Get Google token via popup
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account'
      })

      const result = await signInWithPopup(auth, provider)
      const googleToken = await result.user.getIdToken()

      // Step 2: Send to backend for verification and account creation
      const response = await axios.post<GoogleSignUpResponse>('/api/3/sign-up/google', {
        google_token: googleToken,
        display_name: params.displayName,
        organization_name: params.organizationName,
        project_name: params.projectName
      })

      return response.data
    } catch (err: any) {
      console.error('[useSignUp] Google sign-up error:', err)
      // Handle both string and object error details
      const detail = err.response?.data?.detail
      if (typeof detail === 'string') {
        error.value = detail
      } else if (typeof detail === 'object' && detail?.message) {
        error.value = detail.message
      } else {
        error.value = err.message || 'Failed to sign up with Google'
      }
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear error message
   */
  const clearError = () => {
    error.value = null
  }

  return {
    // State
    isLoading,
    error,

    // Methods
    signUpWithEmail,
    signUpWithGoogle,
    clearError
  }
}
